import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyCheck } from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import { Button, Card, Checkbox, EmptyState, Page, PageHeader, SegmentedControl, Skeleton } from '../components/ui';
import { CandidateCard } from '../features/duplicates/components/CandidateCard';
import { MergeDialog } from '../features/duplicates/components/MergeDialog';
import { Notice } from '../features/duplicates/components/ui';
import { useDuplicateQueue } from '../features/duplicates/useDuplicates';
import { ENTITY_WORD, entityHref, plural } from '../features/duplicates/format';
import { asNumber, asOneOf, useUrlState } from '../hooks/useUrlState';
import type { DuplicateCandidate, DuplicateEntityType, DuplicateQuery, MergeResult } from '../types/duplicates';

const ENTITY_FILTERS: Array<{ value: DuplicateEntityType; label: string }> = [
  { value: 'stakeholder', label: 'People' },
  { value: 'organization', label: 'Organizations' },
];

const SCORE_FILTERS = [
  { value: '80', label: '80+' },
  { value: '60', label: '60+' },
  { value: '40', label: '40+' },
];

/* One codec per parameter, made once: the URL state reads them on every render */
const ENTITY = asOneOf(ENTITY_FILTERS.map((filter) => filter.value));
const MIN_SCORE = asNumber(0, 100);

/** How many pairs one pass may return; not worth a place in the URL */
const SCAN_LIMIT = 50;

/**
 * The review queue for records that look like the same thing twice. Strongest
 * matches first; each pair leaves the queue either as "not duplicates" or as
 * one record.
 */
export const DuplicatesPage: React.FC = () => {
  // What is being reviewed is in the address bar, so a link hands someone else
  // the same queue; the queue hook keeps the rest
  const [entityType, setEntityType] = useUrlState<DuplicateEntityType>('entityType', 'stakeholder', ENTITY);
  const [minScore, setMinScore] = useUrlState('minScore', 60, MIN_SCORE);
  const asked = useMemo<DuplicateQuery>(
    () => ({ entityType, minScore, limit: SCAN_LIMIT }),
    [entityType, minScore],
  );
  const queue = useDuplicateQueue(asked);
  const [merging, setMerging] = useState<DuplicateCandidate | null>(null);
  const [merged, setMerged] = useState<{ result: MergeResult; entityType: DuplicateEntityType } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const dismiss = useCallback(
    async (candidate: DuplicateCandidate) => {
      setActionError(null);
      const failure = await queue.dismiss(candidate);
      if (failure) setActionError(failure);
    },
    [queue],
  );

  const onMerged = useCallback(
    (result: MergeResult) => {
      setMerged({ result, entityType });
      if (merging) queue.forget(merging.id, result.mergedId);
    },
    [entityType, merging, queue],
  );

  const [one, many] = ENTITY_WORD[entityType];
  const meta = queue.scan
    ? `${plural(queue.candidates.length, 'pair')} · ${queue.scan.scanned} ${queue.scan.scanned === 1 ? one : many} scanned`
    : undefined;

  return (
    <DashboardLayout>
      <Page>
        <PageHeader title="Duplicates" meta={meta} />

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SegmentedControl
            label="Kind"
            items={ENTITY_FILTERS}
            value={entityType}
            onChange={(value) => setEntityType(value as DuplicateEntityType)}
          />
          <SegmentedControl
            label="Match score"
            items={SCORE_FILTERS}
            value={String(minScore)}
            onChange={(value) => setMinScore(Number(value))}
          />
          <span className="inline-flex h-9 items-center">
            <Checkbox
              id="show-dismissed"
              checked={!!queue.query.includeDismissed}
              onChange={(event) => queue.setQuery((q) => ({ ...q, includeDismissed: event.target.checked }))}
              label="Show dismissed"
              className="text-neutral-500"
            />
          </span>
        </div>

        {merged && (
          <Notice
            tone="quiet"
            className="mb-3"
            action={
              <Link
                to={entityHref(merged.entityType, merged.result.survivorId)}
                className="text-[13px] text-ink underline decoration-neutral-300 underline-offset-2"
              >
                Open record
              </Link>
            }
          >
            {merged.result.mergedLabel} folded in.
          </Notice>
        )}

        {actionError && (
          <Notice tone="error" className="mb-3">
            {actionError}
          </Notice>
        )}

        {queue.unavailable ? (
          <Card>
            <EmptyState icon={CopyCheck} title="This server cannot find duplicates yet" />
          </Card>
        ) : queue.error ? (
          <Notice tone="error" action={<Button size="xs" variant="secondary" onClick={queue.reload}>Try again</Button>}>
            {queue.error}
          </Notice>
        ) : queue.loading && !queue.scan ? (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full rounded-2xl" />
            <Skeleton className="h-40 w-full rounded-2xl" />
          </div>
        ) : queue.candidates.length === 0 ? (
          <Card>
            <EmptyState icon={CopyCheck} title="Nothing to review" />
          </Card>
        ) : (
          <div className="space-y-3">
            {queue.candidates.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                busy={queue.busyId === candidate.id}
                onDismiss={dismiss}
                onMerge={setMerging}
              />
            ))}
          </div>
        )}
      </Page>

      {merging && (
        <MergeDialog
          open
          candidate={merging}
          onClose={() => setMerging(null)}
          onMerged={onMerged}
        />
      )}
    </DashboardLayout>
  );
};

export default DuplicatesPage;
